<template>
  <!-- Select -->
  <SelectRoot
    v-model="selectedValue"
    v-model:open="isOpen"
    :disabled="disabled || static"
  >
    <div
      class="Dropdown"
      :class="{
        'is-disabled': disabled,
        'is-error': hasError,
        'is-static': static,
        'is-filled': !!selectedValue,
      }"
    >
      <FieldLabel
        v-if="label || $slots.label"
        :required="isRequired"
        :hideAsterisk
        :disabled
      >
        <slot name="label">
          {{ label }}
        </slot>
      </FieldLabel>

      <SelectTrigger :id="name" class="Dropdown-field">
        <SelectValue class="Dropdown-input" :placeholder="placeholder">
          {{ displayedValue }}
        </SelectValue>
        <Icon
          v-show="!(disabled || static)"
          class="Dropdown-chevron"
          :icon="isOpen ? 'chevron-up' : 'chevron-down'"
          :size="20"
        />
      </SelectTrigger>

      <select
        v-if="autocomplete"
        class="u-visually-hidden"
        :name="name"
        :autocomplete="autocomplete"
        tabindex="-1"
        aria-hidden="true"
        @input="onAutofill"
      >
        <option value="" />
        <option
          v-for="option of options"
          :key="String(option.value)"
          :value="String(option.value)"
        >
          {{ option.label }}
        </option>
      </select>

      <SelectPortal>
        <SelectContent
          class="Dropdown-dropdown"
          :sideOffset="4"
          align="start"
          position="popper"
        >
          <div class="Dropdown-dropdownInner">
            <SelectItem
              v-for="option of options"
              :key="option.label"
              :value="option.value"
              :disabled="option.disabled || false"
              asChild
            >
              <DropdownItem
                :text="option.label"
                :leftIcon="option?.leftIcon"
                :rightIcon="option?.rightIcon"
                :active="option.value === selectedValue"
              />
            </SelectItem>
          </div>
        </SelectContent>
      </SelectPortal>

      <FieldHelper
        :errorMessage="errorMessage"
        :helperText="helperText"
      />
    </div>
  </SelectRoot>
</template>

<script setup lang="ts" generic="T extends AcceptableValue = string">
import { computed, ref } from 'vue';
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectPortal,
  SelectContent,
  SelectItem,
  type AcceptableValue,
} from 'reka-ui';
import { useFieldValidation, type FieldProps } from '@cvo/ui/composables';
import FieldLabel from '../../atoms/FieldLabel/FieldLabel.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import FieldHelper from '../../atoms/FieldHelper/FieldHelper.vue';
import DropdownItem from './DropdownItem.vue';
import type { DropdownOption } from './Dropdown.model';

export interface DropdownProps<T extends AcceptableValue = string> extends FieldProps {
  placeholder?: string;
  options?: DropdownOption<T>[];
  autocomplete?: string;
}

const props = withDefaults(defineProps<DropdownProps<T>>(), {
  placeholder: 'Placeholder',
  error: undefined,
  options: () => [],
});

const selectedValue = defineModel<DropdownOption<T>['value'] | undefined>({ default: undefined });

const { hasError, errorMessage, isRequired, hideAsterisk } = useFieldValidation(props);

const displayedValue = computed((): string => {
  const selectedOption = props.options.find(option => option.value === selectedValue.value);
  return selectedOption?.label || props.placeholder || '';
});

const isOpen = ref(false);

function onAutofill(event: Event) {
  const val = (event.target as HTMLSelectElement).value;
  if (!val) {
    selectedValue.value = undefined;
    return;
  }
  const match = props.options.find(opt => String(opt.value) === val);
  selectedValue.value = match ? match.value : undefined;
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Dropdown {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;
    width: 100%;
    max-width: 800px;
    min-width: 64px;
    position: relative;

    // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
    --field-label-color: #{vars.$atoms-dropdown-baseinput-enabled-label-color};

    &-field {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
      outline: none;

      // Default / Enabled
      background-color: vars.$atoms-dropdown-baseinput-enabled-bg-color;
      border: vars.$atoms-dropdown-baseinput-border-stroke solid vars.$atoms-dropdown-baseinput-enabled-border-color;
      border-radius: vars.$atoms-dropdown-baseinput-border-radius;
      padding-block: vars.$atoms-dropdown-baseinput-padding-top-bottom;
      padding-inline: vars.$atoms-dropdown-baseinput-padding-left-right;

      min-height: 29px;
      text-align: start;
      cursor: pointer;
      box-sizing: border-box;

      // Dropdown uses the same element for both placeholder and filled content
      &[data-placeholder],
      .is-disbled &[data-disabled] {
        .Dropdown-input {
           color: vars.$atoms-dropdown-baseinput-placeholder-color;
        }
      }
    }

    &-input {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      font-size: vars.$font-size-sm;
      font-weight: vars.$typography-weight-body;
      line-height: 1;
      letter-spacing: -0.42px;
      width: 100%;
      color: vars.$atoms-dropdown-baseinput-text-color;
    }

    &-chevron {
      color: vars.$atoms-dropdown-baseinput-enabled-icon-color;
    }

    .FieldHelper {
      color: vars.$atoms-dropdown-baseinput-enabled-helper-color;

      &.is-error {
        color: vars.$atoms-dropdown-baseinput-error-helper-color;
      }
    }

    // --- State Overrides ---

    &.is-filled {
      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-dropdown-baseinput-filled-label-color};
      .Dropdown-field {
        background-color: vars.$atoms-dropdown-baseinput-filled-bg-color;
        border-color: vars.$atoms-dropdown-baseinput-filled-border-color;
      }
      .Dropdown-chevron {
        color: vars.$atoms-dropdown-baseinput-filled-icon-color;
      }
      .FieldHelper:not(.is-error) {
        color: vars.$atoms-dropdown-baseinput-filled-helper-color;
      }
    }

    // Focus / Open State
    // Dropdown trigger has data-state="open".
    // Also handling focus-within for keyboard nav.
    &:focus-within,
    &:has(.Dropdown-field[data-state="open"]) {
      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-dropdown-baseinput-focused-label-color};
      .Dropdown-field {
        background-color: vars.$atoms-dropdown-baseinput-focused-bg-color;
        border-color: vars.$atoms-dropdown-baseinput-focused-border-color;
      }
      .Dropdown-chevron {
        color: vars.$atoms-dropdown-baseinput-focused-icon-color;
      }
      .FieldHelper:not(.is-error) {
        color: vars.$atoms-dropdown-baseinput-focused-helper-color;
      }
    }

    &.is-error {
      // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
      --field-label-color: #{vars.$atoms-dropdown-baseinput-error-label-color};
      .Dropdown-field {
        background-color: vars.$atoms-dropdown-baseinput-error-bg-color;
        border-color: vars.$atoms-dropdown-baseinput-error-border-color;
      }
      .Dropdown-chevron {
        color: vars.$atoms-dropdown-baseinput-error-icon-color;
      }
    }

    &.is-disabled {
      --field-label-color: #{vars.$atoms-dropdown-baseinput-disabled-label-color};

      .Dropdown-field {
        background-color: vars.$atoms-dropdown-baseinput-disabled-bg-color;
        border-color: vars.$atoms-dropdown-baseinput-disabled-border-color;
        cursor: not-allowed;
      }

      .Dropdown-chevron {
        color: vars.$atoms-dropdown-baseinput-disabled-icon-color;
      }

      .FieldHelper {
        color: vars.$atoms-dropdown-baseinput-disabled-helper-color;
      }

      &.is-error {
        .Dropdown-field {
          border-color: vars.$atoms-dropdown-baseinput-error-border-color;
        }
        .FieldHelper {
          color: vars.$atoms-dropdown-baseinput-error-helper-color;
        }
      }
    }

    &.is-static {
      --field-label-color: #{vars.$atoms-dropdown-baseinput-static-label-color};

      .Dropdown-field {
        background-color: vars.$atoms-dropdown-baseinput-static-bg-color;
        border-color: vars.$atoms-dropdown-baseinput-static-border-color;
      }

      .Dropdown-chevron {
        color: vars.$atoms-dropdown-baseinput-static-icon-color;
      }

      .FieldHelper {
         color: vars.$atoms-dropdown-baseinput-static-helper-color;
      }
    }

    &-dropdown {
      background-color: vars.$atoms-dropdown-baseitem-default-bg-color;
      border-radius: vars.$border-radius-sm;
      box-shadow: 0px 2px 8px 0px rgba(51, 51, 51, 0.15);
      border: none;
      overflow: hidden;
      min-width: var(--reka-select-trigger-width);
      max-height: 200px;
      overflow-y: auto;

      &Inner {
        display: flex;
        width: 100%;
        flex-direction: column;
      }
    }
  }
}
</style>
